import React from 'react';
import '../styles/legal-pages.css';
import { FaMoon, FaSun } from 'react-icons/fa';

function PrivacyPolicy({ theme, onToggleTheme, onNavigateToLanding }) {
    return (
        <div className="legal-page">
            {/* Header */}
            <header className="legal-header">
                <div className="legal-header-content">
                    <div className="legal-logo">
                        <img src="/orb_dark.png" alt="TeachingOrb icon" width="20" height="20" />
                        <span 
                            style={{ fontFamily: 'Sentient, sans-serif', cursor: 'pointer' }}
                            onClick={onNavigateToLanding}
                        >
                            TeachingOrb
                        </span>
                    </div>
                    <div className="legal-header-actions">
                        {/* Theme toggle button - disabled for now */}
                        {/* {onToggleTheme && (
                            <button className="legal-theme-toggle-btn" onClick={onToggleTheme}>
                                {theme === 'light' ? <FaMoon /> : <FaSun />}
                            </button>
                        )} */}
                        <button 
                            className="legal-back-btn"
                            onClick={onNavigateToLanding}
                        >
                            Back
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="legal-main">
                <div className="legal-container">
                    <section className="legal-section">
                        <h1 className="legal-title">Privacy Policy</h1>
                        <p className="legal-last-updated">Last updated: November 2024</p>

                        <article className="legal-article">
                            <h2 className="legal-heading">1. Introduction</h2>
                            <p>
                                TeachingOrb ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our service.
                            </p>
                            <p>
                                Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our Service.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">2. Information We Collect</h2>
                            <p>
                                We collect information in various ways depending on how you interact with TeachingOrb:
                            </p>
                            <h3 className="legal-subheading">2.1 Account Information</h3>
                            <p>
                                When you create an account, we collect:
                            </p>
                            <ul className="legal-list">
                                <li>Email address</li>
                                <li>Password (encrypted)</li>
                                <li>Name (optional)</li>
                                <li>Profile preferences</li>
                                <li>Theme preferences</li>
                            </ul>
                            <h3 className="legal-subheading">2.2 Usage Information</h3>
                            <p>
                                We automatically collect certain information about your device and usage:
                            </p>
                            <ul className="legal-list">
                                <li>Browser type and version</li>
                                <li>Operating system</li>
                                <li>Courses accessed and completed</li>
                                <li>Time spent on the platform</li>
                                <li>Pages viewed</li>
                                <li>IP address</li>
                                <li>Device identifiers</li>
                            </ul>
                            <h3 className="legal-subheading">2.3 Course Interaction Data</h3>
                            <p>
                                We collect data about your interactions with courses:
                            </p>
                            <ul className="legal-list">
                                <li>Courses requested and generated</li>
                                <li>Course topics and subjects</li>
                                <li>Progress through chapters and sections</li>
                                <li>Quiz and test results</li>
                                <li>Time spent on each section</li>
                            </ul>
                            <h3 className="legal-subheading">2.4 Payment Information</h3>
                            <p>
                                If you subscribe to premium features, payment information is collected by our payment processor (Stripe). We do not store complete credit card information on our servers. We only store transaction records and subscription status.
                            </p>
                            <h3 className="legal-subheading">2.5 Communication Data</h3>
                            <p>
                                If you contact our support team, we collect:
                            </p>
                            <ul className="legal-list">
                                <li>Messages and communications</li>
                                <li>Feedback and suggestions</li>
                                <li>Support tickets and responses</li>
                            </ul>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">3. How We Use Your Information</h2>
                            <p>
                                We use the information we collect for various purposes:
                            </p>
                            <ul className="legal-list">
                                <li>To provide, maintain, and improve our Service</li>
                                <li>To personalize your experience</li>
                                <li>To generate tailored educational content</li>
                                <li>To process transactions and send related information</li>
                                <li>To send promotional communications (with your consent)</li>
                                <li>To respond to your inquiries and support requests</li>
                                <li>To monitor and analyze trends, usage, and activities</li>
                                <li>To detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
                                <li>To comply with legal obligations</li>
                                <li>To improve customer service</li>
                                <li>To personalize and improve your experience</li>
                            </ul>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">4. How We Share Your Information</h2>
                            <p>
                                We do not sell, trade, or rent your personal information to third parties. However, we may share information in the following circumstances:
                            </p>
                            <h3 className="legal-subheading">4.1 Service Providers</h3>
                            <p>
                                We share information with third-party service providers who assist us in operating our Website and conducting our business:
                            </p>
                            <ul className="legal-list">
                                <li>Supabase (database and authentication)</li>
                                <li>AWS Bedrock (AI content generation)</li>
                                <li>Stripe (payment processing)</li>
                                <li>Email service providers</li>
                            </ul>
                            <h3 className="legal-subheading">4.2 Legal Requirements</h3>
                            <p>
                                We may disclose your information when required to do so by law or if we believe in good faith that such action is necessary to:
                            </p>
                            <ul className="legal-list">
                                <li>Comply with a legal obligation</li>
                                <li>Protect and defend the rights or property of TeachingOrb</li>
                                <li>Prevent or investigate possible wrongdoing</li>
                                <li>Protect the personal safety of users or the public</li>
                            </ul>
                            <h3 className="legal-subheading">4.3 Business Transfers</h3>
                            <p>
                                If TeachingOrb is involved in a merger, acquisition, bankruptcy, dissolution, reorganization, similar transaction, or proceeding, your information may be transferred as part of that transaction. We will provide notice before your information becomes subject to a different privacy policy.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">5. Data Security</h2>
                            <p>
                                We implement appropriate technical, administrative, and physical security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
                            </p>
                            <p>
                                Your password is encrypted and never stored in plain text. We use SSL/TLS encryption for data transmission.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">6. Data Retention</h2>
                            <p>
                                We retain your personal information for as long as your account is active or as needed to provide Services. You may request deletion of your account and associated data at any time. However, we may retain certain information for legal, tax, or other legitimate business purposes even after account deletion.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">7. Your Privacy Rights</h2>
                            <p>
                                Depending on your location, you may have certain rights regarding your personal information:
                            </p>
                            <h3 className="legal-subheading">7.1 Access and Portability</h3>
                            <p>
                                You have the right to access your personal information and receive a copy of the data we hold about you in a portable format.
                            </p>
                            <h3 className="legal-subheading">7.2 Correction</h3>
                            <p>
                                You may request correction of inaccurate or incomplete personal information.
                            </p>
                            <h3 className="legal-subheading">7.3 Deletion</h3>
                            <p>
                                You may request deletion of your personal information, subject to certain legal obligations and legitimate business needs.
                            </p>
                            <h3 className="legal-subheading">7.4 Opt-Out</h3>
                            <p>
                                You may opt-out of receiving promotional communications from us by following the unsubscribe instructions in our emails or by updating your account preferences.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">8. Cookies and Tracking Technologies</h2>
                            <p>
                                TeachingOrb uses cookies and similar tracking technologies to enhance your experience. Cookies are small data files stored on your device. You can control cookies through your browser settings, but disabling cookies may affect functionality.
                            </p>
                            <p>
                                We use:
                            </p>
                            <ul className="legal-list">
                                <li>Session cookies (for login sessions)</li>
                                <li>Persistent cookies (for preferences)</li>
                                <li>Analytics cookies (to understand usage)</li>
                            </ul>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">9. Third-Party Links</h2>
                            <p>
                                Our Website may contain links to third-party websites. This Privacy Policy applies only to TeachingOrb. We are not responsible for the privacy practices of other websites. We encourage you to read the privacy policies of any third-party sites before providing personal information.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">10. Children's Privacy</h2>
                            <p>
                                TeachingOrb is not intended for children under 13 years of age (or the applicable age of digital consent in your jurisdiction). We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it promptly.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">11. California Privacy Rights</h2>
                            <p>
                                If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
                            </p>
                            <ul className="legal-list">
                                <li>Right to know what personal information is collected</li>
                                <li>Right to know whether personal information is sold or disclosed</li>
                                <li>Right to delete personal information collected from you</li>
                                <li>Right to opt-out of the sale of personal information</li>
                                <li>Right to non-discrimination for exercising your CCPA rights</li>
                            </ul>
                            <p>
                                To exercise these rights, please contact us using the contact information provided below.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">12. European User Rights (GDPR)</h2>
                            <p>
                                If you are located in the European Union, you have rights under the General Data Protection Regulation (GDPR):
                            </p>
                            <ul className="legal-list">
                                <li>Right of access to your personal data</li>
                                <li>Right to rectification of inaccurate data</li>
                                <li>Right to erasure ("right to be forgotten")</li>
                                <li>Right to restrict processing</li>
                                <li>Right to data portability</li>
                                <li>Right to object to processing</li>
                                <li>Rights related to automated decision-making and profiling</li>
                            </ul>
                            <p>
                                To exercise these rights, please contact us using the contact information below. You also have the right to lodge a complaint with your local data protection authority.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">13. Updates to This Privacy Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by updating the "Last updated" date above and, if the changes are significant, by providing a more prominent notice.
                            </p>
                            <p>
                                Your continued use of TeachingOrb after any modifications indicates your acceptance of the updated Privacy Policy.
                            </p>
                        </article>

                        <article className="legal-article">
                            <h2 className="legal-heading">14. Contact Us</h2>
                            <p>
                                If you have questions about this Privacy Policy or our privacy practices, please contact us at:
                            </p>
                            <p>
                                <strong>TeachingOrb</strong><br />
                                A service offered by Kilowatt Engineering and Design, Inc.<br />
                                Support: support@teachingorb.com
                            </p>
                            <p>
                                We will respond to your inquiry within 30 days of receipt.
                            </p>
                        </article>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="legal-footer">
                <div className="legal-footer-content">
                    <p>&copy; 2025 TeachingOrb. A service offered by Kilowatt Engineering and Design, Inc. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}

export default PrivacyPolicy;

